import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useToast — state and timer for a page's toast; render it with components/v2/Toast.jsx.
 *
 *   const { toast, showToast, dismissToast } = useToast();
 *   showToast('success', 'Saved');
 *   showToast('info', 'Registering…', { persistent: true });   // stays until replaced
 *   <Toast toast={toast} onDismiss={dismissToast} />
 *
 * Argument order is (type, message). Sixteen pages were written (message, type);
 * they keep their call sites and wrap this once — see docs/DUPLICATION_AUDIT.md, B11.
 *
 * Every new toast clears the previous timer. ~15 copies did not, so an older toast's
 * timer dismissed a newer toast early (audit A20).
 *
 * @param {number} [duration=4000] — ms before a non-persistent toast clears
 */
export default function useToast(duration = 4000) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const dismissToast = useCallback(() => {
    clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  /* `options` also accepts a bare boolean for `persistent`, which is how the Users
     page already calls it. */
  const showToast = useCallback((type, message, options) => {
    const persistent = options === true || Boolean(options?.persistent);
    clearTimeout(timerRef.current);
    setToast({ type, message, persistent });
    if (!persistent) timerRef.current = setTimeout(() => setToast(null), duration);
  }, [duration]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { toast, showToast, dismissToast };
}
